import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Affiliation } from '@/types/profile';

import ModalShell from '@/components/modal-shell';
import { ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';


interface Props {
  initial: Affiliation | null;
  onSave: (aff: Affiliation) => void;
  onClose: () => void;
}

const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e.trim());
const ARMED_ROW = `rounded-lg ${ARMED_DELETE_PAINT} -mx-1.5 px-1.5 -my-1 py-1`;

export default function AffiliationModal({ initial, onSave, onClose }: Props) {
  const { t } = useTranslation();
  const [institution, setInstitution] = useState('');
  const [location, setLocation] = useState('');
  const [emails, setEmails] = useState<string[]>(['']);
  const [armedEmail, setArmedEmail] = useState<number | null>(null);

  useEffect(() => {
    if (initial) {
      setInstitution(initial.institution);
      setLocation(initial.location);
      setEmails(initial.emails.map((e) => e.value));
    }
  }, [initial]);

  const validEmails = emails.filter((e) => isValidEmail(e));
  const hasInvalid = emails.some((e) => e.trim().length > 0 && !isValidEmail(e));
  const canSave = institution.trim().length > 0 && location.trim().length > 0 && validEmails.length > 0 && !hasInvalid;

  function handleSave() {
    if (!canSave) return;
    onSave({
      id: initial?.id ?? crypto.randomUUID(),
      institution: institution.trim(),
      location: location.trim(),
      emails: emails.filter((e) => e.trim().length > 0).map((value) => ({ value: value.trim() })),
      active: initial?.active,
      primary: initial?.primary,
    });
  }

  const inputCls = 'h-10 w-full rounded-lg border border-ws-border bg-ws-control px-3 text-sm text-ws-text placeholder:text-ws-muted';
  const labelCls = 'text-xs font-semibold uppercase tracking-wide text-ws-muted';

  return (
    <ModalShell title={initial ? t('profile.editAffiliation') : t('profile.addAffiliation')} onClose={onClose}>
      <div className='flex flex-col gap-1.5'>
        <label htmlFor='aff-institution' className={labelCls}>{t('profile.affiliationModal.institution')}</label>
        <input id='aff-institution' value={institution} onChange={(e) => setInstitution(e.target.value)} placeholder={t('profile.affiliationModal.institutionPlaceholder')} className={inputCls} />
      </div>

      <div className='flex flex-col gap-1.5'>
        <label htmlFor='aff-location' className={labelCls}>{t('profile.affiliationModal.location')}</label>
        <input id='aff-location' value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t('profile.affiliationModal.locationPlaceholder')} className={inputCls} />
      </div>

      <div className='flex flex-col gap-2'>
        <span className={labelCls}>{t('profile.affiliationModal.emails')}</span>
        {emails.map((email, i) => {
          const invalid = email.trim().length > 0 && !isValidEmail(email);
          const isArmed = armedEmail === i;
          return (
            <div key={i} className='flex flex-col gap-1'>
              <div className={`flex items-center gap-2 ${isArmed ? ARMED_ROW : ''}`}>
                <input
                  type='email'
                  aria-label={`${t('profile.affiliationModal.emails')} ${i + 1}`}
                  aria-invalid={invalid}
                  aria-describedby={invalid ? `email-hint-${i}` : undefined}
                  value={email}
                  onChange={(e) => setEmails((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))}
                  placeholder={t('profile.affiliationModal.emailPlaceholder')}
                  className={`${inputCls} flex-1 ${invalid ? 'border-ws-error-fg focus:border-ws-error-fg' : ''}`}
                />
                {emails.length > 1 && (
                  <IconButton
                    type='button'
                    variant='destructive'
                    size='md'
                    isArmed={isArmed}
                    aria-label={isArmed ? t('common.confirmDelete') : t('profile.affiliationModal.removeEmail')}
                    onClick={() => {
                      if (isArmed) { setEmails((prev) => prev.filter((_, j) => j !== i)); setArmedEmail(null); }
                      else setArmedEmail(i);
                    }}
                    onPointerLeave={(e) => { if (isArmed && e.pointerType !== 'touch') setArmedEmail(null); }}
                    onBlur={() => { if (isArmed) setArmedEmail(null); }}
                  >
                    <Trash2 className='h-4 w-4' />
                  </IconButton>
                )}
              </div>
              {invalid && <span id={`email-hint-${i}`} className='pl-1 text-xs text-ws-error-fg'>{t('profile.affiliationModal.invalidEmail')}</span>}
            </div>
          );
        })}
        <button type='button' onClick={() => setEmails((prev) => [...prev, ''])} className='flex w-fit items-center gap-1.5 text-sm font-medium text-ws-text-link hover:underline'>
          <Plus className='h-3.5 w-3.5' />{t('profile.affiliationModal.addEmail')}
        </button>
      </div>

      <div className='flex items-center justify-end gap-3 pt-1'>
        <Button size='lg' onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant='primary' size='lg' onClick={handleSave} disabled={!canSave}>{t('common.done')}</Button>
      </div>
    </ModalShell>
  );
}
